// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// D-CRX-87b A6 (owner 2026-10-21): "Enrollment valid for (days)" — set by the pharmacy itself in CareRx admin
// (also editable from SaaS admin → CareRx). Empty = never expires (today). An expired enrollment takes no more
// prescriptions. Changing it needs the CareRx admin permission; everyone else sees it read-only.

import { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import AppButton from '../../components/ui/AppButton'
import { AppTextField } from '../../components/ui/FormFields'
import LoadState from '../../components/ui/LoadState'
import Section from '../../components/ui/Section'
import StatusChip from '../../components/ui/StatusChip'
import { useToast } from '../../components/ui/Toast'
import { friendlyError } from '../../api/errors'
import { formatUsDateLocal } from '../../api/format'
import type { FeatureSetting } from '../../api/pharmacyFeatures'
import { intProblem } from '../../components/admin/CareRxFeaturesPanel'
import { useMyEditableCareRxFeatures, useSetMyCareRxFeature } from '../../hooks/queries/pharmacyFeatures'

export const VALIDITY_KEY = 'carerx.enrollment_validity_days'

export default function EnrollmentValiditySetting() {
  const q = useMyEditableCareRxFeatures()
  const setting = q.data?.features.find(f => f.key === VALIDITY_KEY)
  return (
    <Section title="Enrollment settings" subtitle="How long an enrollment stays open for more prescriptions of the same drug.">
      <LoadState query={{ ...q, data: setting ? [setting] : q.data ? [] : undefined }} what="the enrollment settings"
        empty={<Box sx={{ fontSize: 12.5, color: 'text.secondary' }}>No enrollment settings for this pharmacy.</Box>}>
        {rows => <ValidityRow f={rows[0]} canEdit={!!q.data?.can_edit} />}
      </LoadState>
    </Section>
  )
}

function ValidityRow({ f, canEdit }: { f: FeatureSetting; canEdit: boolean }) {
  const toast = useToast()
  const set = useSetMyCareRxFeature()
  const saved = typeof f.value === 'number' ? String(f.value) : ''
  const [text, setText] = useState(saved)
  useEffect(() => setText(saved), [saved])
  const problem = intProblem(text, f.min, f.max)
  const dirty = text.trim() !== saved
  const save = (value: number | null) => set.mutate({ key: f.key, value }, {
    onSuccess: s => toast.show(s.overridden ? 'Enrollment validity saved.' : 'Enrollments never expire now.', 'success'),
    onError: e => toast.show(friendlyError(e), 'error'),
  })
  return (
    <Box data-feature={f.key}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <AppTextField size="small" label="Enrollment valid for (days)" value={text} disabled={!canEdit || set.isPending}
          sx={{ width: 220 }} inputProps={{ inputMode: 'numeric' }} error={!!problem}
          helperText={problem ?? (text.trim() === '' ? 'Empty = never expires' : ' ')}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !problem && dirty) { e.preventDefault(); save(text.trim() === '' ? null : Number(text.trim())) } }} />
        {canEdit && (
          <AppButton size="small" variant="contained" disabled={!!problem || !dirty || set.isPending}
            onClick={() => save(text.trim() === '' ? null : Number(text.trim()))}>Save</AppButton>)}
        <StatusChip status={f.overridden ? 'overridden' : 'default'} label={f.overridden ? `${f.value} days` : 'Never expires'}
          tone={f.overridden ? 'blue' : 'grey'} />
      </Stack>
      <Box sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
        Counted from the day the enrollment was created. An expired enrollment takes no more prescriptions; a new
        prescription then starts a new enrollment.
        {f.overridden && f.updated_at ? ` Set by ${f.updated_by_name ?? 'a user'} on ${formatUsDateLocal(f.updated_at)}.` : ''}
        {!canEdit && ' Ask a CareRx admin to change it.'}
      </Box>
    </Box>
  )
}
